import './StatementsStrip.scss'
import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { accountPath, formatCurrency, statementMonths, statementYear } from '../../Helpers'

interface Props {
    account: Account
    // Du plus récent au plus ancien, comme Account.statements.
    statements: Statement[]
    filename: Filename
}

const monthIndex = (statement: Statement) => statement.period
    ? statement.period.year * 12 + statement.period.end_month
    : null

const formatRoundedCurrency = (amount_cents: number) => formatCurrency(Math.round(amount_cents / 100) * 100).replace(/,00/, '')

// Frise des relevés d'un compte, du plus ancien au plus récent : deux barres par relevé, impact budget
// (après ratio d'impact) et impact cash (somme des opérations), à la même échelle. Des pointillés
// marquent les mois sans relevé.
const StatementsStrip = ({ account, statements, filename }: Props) => {
    const stripRef = useRef<HTMLDivElement>(null)
    const selectedRef = useRef<HTMLAnchorElement>(null)
    const chronological = [...statements].reverse()
    const maxTotal = Math.max(1, ...statements.flatMap((statement) => [Math.abs(statement.total_cents), Math.abs(statement.budget_cents)]))
    const barHeight = (amount_cents: number) => Math.max(4, Math.round(80 * Math.abs(amount_cents) / maxTotal))

    useEffect(() => {
        const strip = stripRef.current, selected = selectedRef.current
        if(strip && selected) {
            strip.scrollLeft = selected.offsetLeft - strip.clientWidth / 2 + selected.clientWidth / 2
        }
    }, [filename, statements.length])

    return <div className="tl-card statements-strip" ref={stripRef}>
        { chronological.map((statement, i) => {
            const previous = chronological[i - 1]
            const current = monthIndex(statement), before = previous && monthIndex(previous)
            const gap = current !== null && before !== null && before !== undefined && current - before > 1
            const selected = statement.filename === filename

            return [
                gap && <div key={`gap-${statement.filename}`} className="gap" title={`${current - before - 1} mois sans relevé`}>···</div>,
                <Link
                    key={statement.filename}
                    ref={selected ? selectedRef : undefined}
                    to={accountPath(account, statement.filename)}
                    className={`statement${selected ? ' selected' : ''}`}
                    title={`${statement.filename} · ${statement.count} opérations · budget ${formatCurrency(statement.budget_cents)} · cash ${formatCurrency(statement.total_cents)}${statement.to_categorize_count > 0 ? ` · ${statement.to_categorize_count} à catégoriser` : ''}`}>
                    { statement.to_categorize_count > 0 && <span className="pending-dot"/> }
                    <span className="totals">
                        <span className="budget">{ formatRoundedCurrency(statement.budget_cents) }</span>
                        <span className="cash">{ formatRoundedCurrency(statement.total_cents) }</span>
                    </span>
                    <span className="bars">
                        <span className="bar budget" style={{ height: barHeight(statement.budget_cents) }}/>
                        <span className="bar cash" style={{ height: barHeight(statement.total_cents) }}/>
                    </span>
                    <span className="months">{ statementMonths(statement) }</span>
                    <span className="year">{ statementYear(statement) }</span>
                </Link>
            ]
        }) }
    </div>
}

export default StatementsStrip
